import { Button } from "@rosette/web_components/src/ui/Button.js";
import { RoyalBanner } from "@/components/social/banner/RoyalBanner.js";
import type { MutableRefObject } from "react";
import type { Path } from "@rosette/ts/src/util/Path.js";
import { useGameController, useGameSource } from "@/components/game/GameUIContext.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { GameThemeType } from "@rosette/ts/src/business/game/theme/GameThemeType.js";
import { writeBrowserStorage } from "@rosette/web_utils/src/storage/browserStorage.js";
import { isDebugEnabled } from "@rosette/ts/src/business/debug.js";
import { LocalComputerGameSource } from "@rosette/ts/src/business/game/controller/source/LocalComputerGameSource.js";
import { ConfirmButton } from "@/components/game/menu/ConfirmButton.js";
import { MENU_BUTTON, MenuOptions } from "@/components/game/menu/MenuLayout.js";
import { Card } from "@rosette/web_components/src/ui/Card.js";


function getDefaultThemeType(): GameThemeType {
    const now = new Date();
    for (const themeType of GameThemeType.values()) {
        if (themeType.isInDateRange(now))
            return themeType;
    }
    return GameThemeType.DEFAULT;
}


interface ActiveGameMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    showPreferences?: () => void;
    showRules?: () => void;
    showStats?: () => void;
    aboutURL?: Path;
    aboutLabel?: string;
    backToHome?: () => void;
}


export function ActiveGameMenu({
    contentRef, showPreferences, showRules, showStats, aboutURL, aboutLabel,
    backToHome,
}: ActiveGameMenuProps) {

    const controller = useGameController();
    const gameSource = useGameSource();
    const game = useRune(controller.getGame());
    const defaultThemeType = getDefaultThemeType();
    const themeType = useRune(controller.getTheme());

    let themeButtonText: string | null = null;

    if (themeType === GameThemeType.HALLOWEEN) {
        themeButtonText = "Disable Halloween Mode 🎃";
    } else if (themeType === GameThemeType.HOLIDAY) {
        themeButtonText = "Disable Holiday Mode 🎅";
    } else {
        if (defaultThemeType === GameThemeType.HALLOWEEN) {
            themeButtonText = "Enable Halloween Mode 🎃";
        } else if (defaultThemeType === GameThemeType.HOLIDAY) {
            themeButtonText = "Enable Holiday Mode 🎅";
        }
    }

    return (
        <MenuOptions contentRef={contentRef}>
            <Card className="max-w-[24em] px-6 py-3 md:py-3">
                <RoyalBanner
                    className="mb-0"
                    noLink={true} />
            </Card>

            {showStats && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={showStats}>

                    Player Stats
                </Button>

            )}

            {showRules && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={showRules}>

                    Game Rules
                </Button>
            )}

            {showPreferences && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={showPreferences}>

                    Preferences
                </Button>
            )}

            {themeButtonText !== null && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={() => {
                        const newThemeType = (
                            themeType !== GameThemeType.DEFAULT
                                ? GameThemeType.DEFAULT
                                : defaultThemeType
                        );
                        controller.getTheme().set(newThemeType);

                        if (newThemeType === GameThemeType.DEFAULT) {
                            writeBrowserStorage("disabled-theme-type", themeType.getName());
                        } else {
                            writeBrowserStorage("disabled-theme-type", null);
                        }
                    }}>

                    {themeButtonText}
                </Button>
            )}

            {isDebugEnabled() && gameSource instanceof LocalComputerGameSource && !game.isFinished() && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={() => gameSource.completeInstantly()}>

                    Complete Game
                </Button>
            )}

            {aboutURL && aboutLabel && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    href={aboutURL.toString()}
                    target="_blank">

                    {aboutLabel}
                </Button>
            )}

            {backToHome && (
                <ConfirmButton
                    onSubmit={backToHome}
                    skipConfirm={game.isFinished()}>

                    Back to Home
                </ConfirmButton>
            )}
        </MenuOptions>
    );
}
